import { Controller } from "@hotwired/stimulus"

// contact_info.html.erb (registration step) の電話番号 / メール validation + 利用規約 /
// プライバシーポリシー 2 modal 制御 controller (#1833 PR 8c)。
//
// 旧 inline JS の構造を 1 controller に集約:
// 1. 電話番号: 全角→半角変換 + 数字以外除去 + 11 文字制限 + counter 表示 + 070/080/090 prefix 検証
// 2. メール: 連続ドット / 先頭末尾ドット禁止 / ドメイン構造の strict regex
// 3. 両方 valid な時のみ submit を enable
// 4. 利用規約 / プライバシーポリシー modal の open / close / 背景 click close
//
// scope: 全 content (form + modals) を wrap する div に置く。form 自体を scope にすると
// modal が form 外配置 (form と sibling) のため scope 外になる問題への対応 (memory
// `reference_stimulus_migration_benefits` の Controller scope 外 element への参照 section
// 参照、本 PR は wrapper-scope 化 (DOM scope 越え契約とは別 approach) で解決)。
//
// XSS 安全性: textContent / classList のみ、innerHTML 不使用。phoneError.textContent に
// 流れる文字列は controller 内 literal (validation message)、user input 不介在。
//
// Rule of Three: contact_info 1 view 1 instance、phone regex / domain / class 名は
// hardcoded で問題なし。将来 modal が他 view にも出てきたら modal 部分を別 controller に
// 抽出する判断軸。
export default class extends Controller {
  static targets = [
    "phoneInput", "phoneError", "phoneCounter",
    "emailInput", "submit",
    "termsModal", "privacyModal",
  ]

  connect() {
    this.validateForm()
    // Esc key で modal close を実現するため document-level keydown listener を attach
    // (#1869 a11y: keyboard 利用者 / screen reader user / power user が期待する standard UX、
    // WAI-ARIA Authoring Practices の `dialog` pattern)。
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
  }

  // Esc key で open 中 modal を close、Tab key で modal 内 focus を trap (#1869 focus trap)。
  // 両 modal が open している scenario は設計上発生しない (1 trigger ごとに 1 modal open) ため、
  // 最初に hidden でない modal を選んで処理。modal closed 時は no-op。
  handleKeydown(event) {
    const openModal = this.#openModalEl()
    if (!openModal) return
    if (event.key === "Escape") {
      this.#closeOpenModal(openModal)
    } else if (event.key === "Tab") {
      this.#trapTabInModal(openModal, event)
    }
  }

  // 電話番号 input: 全角→半角 + 数字以外除去 + 11 文字制限 + counter 更新 → validateForm()
  // (旧 inline JS の `validatePhoneNumber` parity)
  validatePhone() {
    const value = this.#normalizePhone(this.phoneInputTarget.value)
    this.phoneInputTarget.value = value
    if (this.hasPhoneCounterTarget) {
      this.phoneCounterTarget.textContent = `${value.length}/11`
    }
    this.validateForm()
  }

  // メール input: validateForm() のみ呼ぶ (raw value の正規化なし)
  validateEmail() {
    this.validateForm()
  }

  validateForm() {
    const phoneValid = this.#validateAndShowPhoneError()
    const emailValid = this.#isEmailValid()
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !(phoneValid && emailValid)
    }
  }

  // ---------- modal: 利用規約 ----------
  // 背景 click → close は view 側で **gray overlay div 自身**に `closeTermsModal` action
  // を attach する形で実現 (#1867 で修正)。旧 `closeTermsModalOnBackground` (outer div
  // で `event.target === event.currentTarget` 判定) は実質発火しない構造だった bug を
  // 解消、判定ロジック自体を撤去。
  //
  // a11y (#1869): open 時に opener element を `lastFocusedElement` に記録、close 時に
  // focus を opener に復元することで、keyboard / screen reader 利用者が modal 開閉後に
  // navigation context を失わない UX を保証 (WAI-ARIA Authoring Practices の dialog pattern)。

  openTermsModal(event) {
    event.preventDefault()
    this.lastFocusedElement = document.activeElement
    this.#showModal(this.termsModalTarget)
  }

  closeTermsModal() {
    this.#hideModal(this.termsModalTarget)
    this.lastFocusedElement?.focus()
    this.lastFocusedElement = null
  }

  // ---------- modal: プライバシーポリシー ----------

  openPrivacyModal(event) {
    event.preventDefault()
    this.lastFocusedElement = document.activeElement
    this.#showModal(this.privacyModalTarget)
  }

  closePrivacyModal() {
    this.#hideModal(this.privacyModalTarget)
    this.lastFocusedElement?.focus()
    this.lastFocusedElement = null
  }

  // ---------- modal helpers (#1869 a11y: aria-hidden 動的切替 + focus trap) ----------
  // modal open: 該当 modal に `aria-hidden="false"` + background content に `aria-hidden="true"`
  // → screen reader が modal だけを認識 (background が読み上げられない) + modal 内 first focusable
  // に focus 移動 (focus trap の起点)。
  // modal close: 逆を実行。
  // `aria-modal="true"` は modal element に静的指定済だが、screen reader / browser の互換性
  // 確保のため `aria-hidden` 動的切替も併用 (WAI-ARIA dialog pattern 標準)。

  #showModal(modalEl) {
    modalEl.classList.remove("hidden")
    modalEl.setAttribute("aria-hidden", "false")
    this.#setBackgroundAriaHidden(true)
    // focus trap の起点: modal open 直後に first focusable element に focus 移動
    // (focus が外に残ったまま modal が開く UX を防ぐ、WAI-ARIA dialog pattern)。
    this.#focusableElementsIn(modalEl)[0]?.focus()
  }

  #hideModal(modalEl) {
    modalEl.classList.add("hidden")
    modalEl.setAttribute("aria-hidden", "true")
    this.#setBackgroundAriaHidden(false)
  }

  // ---------- focus trap helpers (#1869 focus trap) ----------
  // open 中の modal element を返す。両 modal が同時 open は設計上 0% scenario のため terms 優先。
  #openModalEl() {
    if (this.hasTermsModalTarget && !this.termsModalTarget.classList.contains("hidden")) {
      return this.termsModalTarget
    }
    if (this.hasPrivacyModalTarget && !this.privacyModalTarget.classList.contains("hidden")) {
      return this.privacyModalTarget
    }
    return null
  }

  // open 中 modal を identify して対応する close method を呼ぶ。Esc key handler から使用。
  #closeOpenModal(modalEl) {
    if (modalEl === this.termsModalTarget) this.closeTermsModal()
    else if (modalEl === this.privacyModalTarget) this.closePrivacyModal()
  }

  // Tab / Shift+Tab で modal 内 focus を循環させる。
  // - focus が modal 外にある場合 (何らかの理由で focus が modal を escape していた): preventDefault +
  //   first focusable に recapture。focus trap の robustness を担保 (modalEl.contains で判定)
  // - focus が last focusable で Tab / first focusable で Shift+Tab: preventDefault + 反対端に wrap
  // - focus が modal 内中間にある時: default 挙動 (browser が次 focusable に移動) を尊重
  // - focusable 0 個の edge case (validation 等で disabled になった場合): Tab を抑止して focus が
  //   modal 外に逃げないよう保証
  #trapTabInModal(modalEl, event) {
    const focusables = this.#focusableElementsIn(modalEl)
    if (focusables.length === 0) {
      event.preventDefault()
      return
    }
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement
    if (!modalEl.contains(active)) {
      event.preventDefault()
      first.focus()
      return
    }
    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  // modal element 内の focusable element を tab order に従って収集。
  // 標準的な focusable selector + `:not([disabled])` filter + `tabindex="-1"` 除外。
  // modal は open 時のみこの helper を経由するため `display: none` filter は不要 (modal 自身が
  // visible)。selector に iframe を含めるのは privacy modal の iframe focus も対象にするため。
  #focusableElementsIn(modalEl) {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      'iframe',
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ")
    return Array.from(modalEl.querySelectorAll(selector))
  }

  // background content (`data-layout-background` marker、`_layout_lp` の min-h-screen div) に
  // aria-hidden を toggle。`data-layout-background` を選んだ理由: layout_lp が registration step
  // 全 view で共有される partial、controller 名に依存しない generic な marker にすることで
  // 他 view / 他 controller でも同 a11y pattern を再利用しやすい。
  #setBackgroundAriaHidden(hidden) {
    const bg = this.element.querySelector("[data-layout-background]")
    if (!bg) return
    if (hidden) {
      bg.setAttribute("aria-hidden", "true")
    } else {
      bg.removeAttribute("aria-hidden")
    }
  }

  // ---------- private helpers ----------

  // 全角数字 (０-９) → 半角 + 数字以外除去 + 11 文字制限
  #normalizePhone(raw) {
    const halfWidth = raw.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
    return halfWidth.replace(/[^0-9]/g, "").slice(0, 11)
  }

  // **side effect あり**: phone error DOM の表示/非表示を切替えつつ validity bool を返す。
  // `#isPhoneValid` 命名だと pure check を連想させて誤誘導するため、`#validateAndShowPhoneError`
  // で副作用を明示 (#1859 review Suggestion)。`#isEmailValid` は pure check で対称的に区別。
  #validateAndShowPhoneError() {
    if (!this.hasPhoneInputTarget) return false
    const value = this.phoneInputTarget.value

    if (this.hasPhoneErrorTarget) this.phoneErrorTarget.classList.add("hidden")

    if (value.length === 0) {
      // 未入力: error 非表示 + invalid
      return false
    } else if (value.length < 11) {
      this.#showPhoneError("携帯電話番号は11文字で入力してください")
      return false
    } else if (!/^(070|080|090)/.test(value)) {
      this.#showPhoneError("070、080、090で始まる携帯電話番号を入力してください")
      return false
    }
    return true
  }

  #showPhoneError(message) {
    if (!this.hasPhoneErrorTarget) return
    this.phoneErrorTarget.textContent = message
    this.phoneErrorTarget.classList.remove("hidden")
  }

  // メール: 連続ドット / 先頭末尾ドット禁止 / ドメイン構造の strict regex (旧 parity)
  #isEmailValid() {
    if (!this.hasEmailInputTarget) return false
    const email = this.emailInputTarget.value.trim()
    if (email === "") return false
    const pattern = /^(?!.*\.\.)(?!\.)(?!.*\.$)[a-zA-Z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/
    return pattern.test(email)
  }
}
